import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PersonRemoveSharpIcon from "@mui/icons-material/PersonRemoveSharp";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import {
  Button,
  Card,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { ITableProps, kaReducer, Table } from "ka-table";
import {
  deselectAllVisibleRows,
  hideDetailsRow,
  hideLoading,
  selectAllVisibleRows,
  showDetailsRow,
  showLoading,
  updateData,
} from "ka-table/actionCreators";
import { DataType } from "ka-table/enums";
import { ChildComponents } from "ka-table/models";
import { ICellTextProps, IHeadCellProps } from "ka-table/props";
import { DispatchFunc } from "ka-table/types";
import { kaPropsUtils } from "ka-table/utils";
import React, { useContext, useEffect, useState } from "react";

import { assignedApp } from "dto/appDto";
import DetailsRow from "pages/users/components/DetailsRow";
import {
  getAssignedApplications,
  subscribeNotificationToApp,
  unsubscribeNotificationFromApp
} from "services/userServices";
import { selectAuth } from "slices/authSlice";
import { useAppSelector } from "store/hooks";
import dialogContext, { DialogType } from "utilities/dialogContext";
import { Log } from "utilities/helper";

import "pages/users/components/UserListTable.scss";
import { setIsLoading } from "src/slices/crashSlice";

const ITEM_HEIGHT = 48;

type CustomCellProps = {
  rowData: ICellTextProps["rowData"];
};

const AssignedApplicationListTable = (props: any) => {
  const theme = useTheme();

  const auth = useAppSelector(selectAuth);
  const [assignedApps, setAssignedApps] = useState<assignedApp[]>([]);

  const SelectionHeader: React.FC<IHeadCellProps> = ({
    dispatch,
    areAllRowsSelected,
  }) => {
    return (
      <input
        type="checkbox"
        checked={areAllRowsSelected}
        onChange={(event) => {
          if (event.currentTarget.checked) {
            dispatch(selectAllVisibleRows()); // also available: selectAllFilteredRows(), selectAllRows()
          } else {
            dispatch(deselectAllVisibleRows()); // also available: deselectAllFilteredRows(), deselectAllRows()
          }
        }}
      />
    );
  };
  
  const CustomCell = ({ rowData }: CustomCellProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const auth = useAppSelector(selectAuth);
    var {
      setDialogType,
      setShowDialog,
      setTitle,
      setMessage,
      setOnClickOk,
      setOnClickCancel,
    } = useContext(dialogContext);
  
    function unsubscribeNotification() {
      setTitle("Unsubscribe Notification");
      setMessage(
        `You are about to unsubscribe to "${rowData.column2}" notifications. Are you sure?`
      );
      setDialogType(DialogType.CONFIRMATION);
      setOnClickCancel(() => () => setShowDialog(false));
  
      setOnClickOk(() => () => {
        unsubscribeNotificationFromApp(auth.authToken, rowData.id, auth.id).then((response) => {
          var title = "";
          var message = "";
          if (response[0]) {
            var result = response[0];
            message = result.message;
            if (result.success) {
              title = "Success!";
              fetchTableData();
            } else {
              title = "Error!";
            }
          } else {
            title = "Error";
            message = "Please try again later";
            var error = response[1];
            Log(error.message);
          }
  
          setTitle(title);
          setMessage(message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
        });
      });
  
      setShowDialog(true);
    }

    function subscribeNotification() {
      setTitle("Subscribe Notification");
      setMessage(
        `You are about to subscribe to "${rowData.column2}" notifications. Are you sure?`
      );
      setDialogType(DialogType.CONFIRMATION);
      setOnClickCancel(() => () => setShowDialog(false));
  
      setOnClickOk(() => () => {
        subscribeNotificationToApp(auth.authToken, rowData.id, auth.id).then((response) => {
          var title = "";
          var message = "";
          if (response[0]) {
            var result = response[0];
            message = result.message;
            if (result.success) {
              title = "Success!";
              fetchTableData();
            } else {
              title = "Error!";
            }
          } else {
            title = "Error";
            message = "Please try again later";
            var error = response[1];
            Log(error.message);
          }
  
          setTitle(title);
          setMessage(message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
        });
      });
  
      setShowDialog(true);
    }
  
    return (
      <>
        {rowData.isSubscribed ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<PersonRemoveSharpIcon fontSize="small" />}
            onClick={() => unsubscribeNotification()}
            sx={{
              position: "inherit",
              m: 0.5,
              whiteSpace: "nowrap",
            }}
          >
            Unsubscribe Notification
          </Button>
        ) : (
          <Button
            variant="contained"
            color="info"
            startIcon={<PersonAddAlt1Icon fontSize="small" />}
            onClick={() => subscribeNotification()}
            sx={{
              position: "inherit",
              m: 0.5,
              whiteSpace: "nowrap",
            }}
          >
            Subscribe Notification
          </Button>
        )}
      </>
    );
  };

  const DetailsButton: React.FC<ICellTextProps> = ({
    dispatch,
    rowKeyValue,
    isDetailsRowShown,
  }) => {
    return (
      <Tooltip title={isDetailsRowShown ? "Hide Details" : "Show Details"}>
        <IconButton
          disableRipple
          sx={{
            position: "inherit",
            "&:hover": { background: theme.colors.info.lighter },
            color: theme.palette.info.main,
          }}
          onClick={() => {
            dispatch(
              isDetailsRowShown
                ? hideDetailsRow(rowKeyValue)
                : showDetailsRow(rowKeyValue)
            );
          }}
        >
          {isDetailsRowShown ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
        </IconButton>
      </Tooltip>
    );
  };

  const ChildComponents: ChildComponents = {
    noDataRow: {
      content: () => "No Data Found",
    },

    pagingIndex: {
      elementAttributes: ({ isActive }) => ({
        className: `page-item waves-effect ${isActive ? "active" : ""}`,
      }),
    },

    pagingSize: {
      elementAttributes: ({ value }) => ({
        className: `page-item waves-effect ${value === option.paging?.pageSize ? "active" : ""
          }`,
      }),
    },

    pagingSizes: {
      elementAttributes: () => ({
        className: "pagination",
      }),
    },

    pagingPages: {
      elementAttributes: () => ({
        className: "pagination",
      }),
    },

    cellText: {
      content: (props) => {
        switch (props.column.key) {
          case "column5":
            return <CustomCell rowData={props.rowData} />;
        }
      },
    },

    detailsRow: {
      elementAttributes: () => ({
        style: {
          backgroundColor: "#eee",
        },
      }),
      content: DetailsRow,
    },

    tableWrapper: {
      elementAttributes: () => ({ style: { maxHeight: "60vh" } }),
    },

    filterRowCell: {
      content: (props) => {
        if (props.column.key === "selection-cell") {
          return <></>;
        }
      },
    },
    headCell: {
      content: (props) => {
        if (props.column.key === "selection-cell") {
          return (
            <SelectionHeader
              {...props}
              areAllRowsSelected={
                kaPropsUtils.areAllVisibleRowsSelected(option) &&
                kaPropsUtils.getSelectedData(option).length > 0
              }
            // areAllRowsSelected={kaPropsUtils.areAllFilteredRowsSelected(tableProps)}
            />
          );
        }
      },
    },
  };

  let dataArray = assignedApps.map((ListItem) => ({
    column1: ListItem.applicationId,
    column2: ListItem.applicationName,
    column4: ListItem.description,
    id: ListItem.applicationId,
    isSubscribed: ListItem.isSubscribed
  }));

  let tableOption: ITableProps = {
    columns: [
      {
        key: "column1",
        title: "Id",
        width: 180,
        style: { textAlign: "center" },
        dataType: DataType.Number,
      },
      {
        key: "column2",
        title: "Application Name",
        width: 250,
        dataType: DataType.String,
      },
      {
        key: "column4",
        title: "Description",
        width: 100,
        dataType: DataType.String,
      },
      {
        key: "column5",
        title: "Action",
        width: 170,
        style: { textAlign: "center" },
      },
    ],
    data: dataArray,
    rowKeyField: "id",
  };

  const [option, changeOptions] = useState(tableOption);

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(() => {
    updateTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedApps]);

  const fetchTableData = () => {
    dispatch(showLoading());
    getAssignedApplications(
      auth.authToken,
      auth.id
    ).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setAssignedApps(result.data);
        } else {
          Log(result.message);
        }
      } else {
        var error = response[1];
        Log(error.message);
      }
    });
    dispatch(hideLoading());
  };

  const updateTable = () => {
    setIsLoading(true);
    dataArray = assignedApps.map((ListItem) => ({
      column1: ListItem.applicationId,
      column2: ListItem.applicationName,
      column4: ListItem.description,
      id: ListItem.applicationId,
      isSubscribed: ListItem.isSubscribed
    }));
    dispatch(updateData(dataArray));
    setIsLoading(false);
  }

  const dispatch: DispatchFunc = (action) => {
    changeOptions((prevState: ITableProps) => kaReducer(prevState, action));
  };

  return (
    <Card sx={{ mt: 2, mb: 2 }}>
      <div className="ka-table-user">
        <Table
          {...option}
          dispatch={dispatch}
          childComponents={ChildComponents}
        />
      </div>
    </Card>
  );
};

export default AssignedApplicationListTable;
